import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Id, toast } from "react-toastify";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";

import ToastData from "components/ToastData";

import { useGetBUUnassignedMember } from "../UseCase/useGetBUEmployeeMember";
import { queryGetBUEmployeeMember } from "@type/api/businessUnit";
import { mutationAddAssignmentAll } from "@type/api/businessUnit/addAssignmentAll";
import { mutationAddAssigmentMember } from "@type/api/businessUnit/addAssignmentMember";
import { useTranslationContext } from "context/TranslationContext";
import { useGetAccess } from "hooks/useGetAccess";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { useTableQueryParams } from "hooks/useTableQueryParams";

import { TGetBUUnassignedMemberFilter } from "../Model/types";
import {
  TBUEmployeeMember,
  TUnassignedBUMember,
} from "@type/api/businessUnit/types";
import { TSort, TTableRecord } from "components/Table/types";
import {
  DEFAULT_BU_UNASSIGNED_MEMBER,
  DEFAULT_SORT_UNASSIGNED_MEMBER,
  SORT_MAP_UNASSIGNED_MEMBER,
} from "../Model/constants";
import { DEFAULT_PAGINATION_REQ } from "@type/api/constants";
import { DEFAULT_PAGINATION } from "components/Table/constants";
import { BU_ACCESS_RIGHT } from "constants/accessRight";
import { TOAST_DATA } from "constants/index";

export const useUnassignedAccountController = () => {
  const { t } = useTranslationContext();
  const navigate = useNavigate();
  const toastId = useRef<Id>("unassigned-bu-account");
  const { buSlug = "" } = useParams();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isSelectedAllPage, setIsSelectedAllPage] = useState(false);
  const [openEmployeeMemberModal, setOpenEmploeeMemberModal] = useState(false);
  const [openAssignConfirmationModal, setOpenAssignConfirmationModal] =
    useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<TBUEmployeeMember>();
  const [selectedUnassignedMember, setSelectedUnassignedMember] =
    useState<TUnassignedBUMember>();
  const [searchEmployeeKeyword, setSearchEmployeeKeyword] = useState("");
  const employeeListContainerRef = useRef<HTMLDivElement>(null);

  const { givenAccess: hasAssignStaffAccess } = useGetAccess({
    requiredAccessRights: [BU_ACCESS_RIGHT.ASSIGN_STAFF],
  });

  const { searchParams, onSetSearchParams } =
    useTableQueryParams<TGetBUUnassignedMemberFilter>({
      sortMap: SORT_MAP_UNASSIGNED_MEMBER,
      defaultData: {
        sort: DEFAULT_SORT_UNASSIGNED_MEMBER,
        filter: {
          ...DEFAULT_BU_UNASSIGNED_MEMBER,
          idBu: buSlug || "",
        },
      },
    });

  const sort = useMemo(() => {
    const splitedSort = searchParams?.constructedSort?.split(" ");
    if (splitedSort.length < 2) return {};

    return {
      sortKey: splitedSort[0],
      sortType: splitedSort[1],
    };
  }, [searchParams?.constructedSort]);

  const {
    isLoadingGetBUUnassignedMember,
    buUnassignedMembers,
    refetchBUUnassignedMember,
  } = useGetBUUnassignedMember({
    params: {
      idBu: searchParams.filter?.idBu || "",
      data: {
        ...searchParams?.filter,
        ...searchParams?.page,
        ...sort,
      },
    },
    onSuccess: (res) => {
      setPagination((prev) => ({
        ...prev,
        currentPage: res.page,
        itemPerPage: res.pageSize,
        totalItem: res.total,
      }));
    },
  });

  const { mutate: mutateAddAssigmentMember } = useMutation({
    mutationFn: mutationAddAssigmentMember,
    onMutate: () => {
      toastId.current = toast.loading(
        t("modules.assign_bu_member.add_member_assignment.pending")
      );
    },
    onSuccess: () => {
      toast.update(toastId.current, {
        render: (
          <ToastData
            message={t(
              "modules.assign_bu_member.add_member_assignment.success"
            )}
          />
        ),
        ...TOAST_DATA("success"),
      });
      handleCloseEmployeeMemberModal();
      handleCloseAssignConfirmationModal();
      refetchBUUnassignedMember();
      setSelectedRows([]);
    },
    onError: (error) => {
      toast.update(toastId.current, {
        render: (
          <ToastData
            message={t("modules.assign_bu_member.add_member_assignment.failed")}
            error={error}
          />
        ),
        ...TOAST_DATA("error"),
      });
    },
  });

  const { mutate: mutateAddAssigmentAll } = useMutation({
    mutationFn: mutationAddAssignmentAll,
    onMutate: () => {
      toastId.current = toast.loading(
        t("modules.assign_bu_member.add_member_assignment.pending")
      );
    },
    onSuccess: () => {
      toast.update(toastId.current, {
        render: (
          <ToastData
            message={t(
              "modules.assign_bu_member.add_member_assignment.success"
            )}
          />
        ),
        ...TOAST_DATA("success"),
      });
      handleCloseEmployeeMemberModal();
      handleCloseAssignConfirmationModal();
      refetchBUUnassignedMember();
      setSelectedRows([]);
    },
    onError: (error) => {
      toast.update(toastId.current, {
        render: (
          <ToastData
            message={t("modules.assign_bu_member.add_member_assignment.failed")}
            error={error}
          />
        ),
        ...TOAST_DATA("error"),
      });
    },
  });

  const {
    data: employeeData,
    isLoading: isLoadingEmployeeData,
    hasNextPage: employeeDataHasNextPage,
    isFetchingNextPage: isFetchingEmployeeDataNextPage,
    fetchNextPage: fetchEmployeeDataNextPage,
  } = useInfiniteQuery({
    enabled: true,
    queryKey: queryGetBUEmployeeMember._key({
      idBu: buSlug || "",
      data: {
        ...DEFAULT_PAGINATION_REQ,
        keyword: searchEmployeeKeyword,
      },
    }),
    queryFn: ({ pageParam, queryKey }) => {
      const idBu = typeof queryKey[1] === "object" ? queryKey[1]?.idBu : "";
      const data = typeof queryKey[1] === "object" ? queryKey[1]?.data : {};

      return queryGetBUEmployeeMember({
        idBu,
        data: {
          ...data,
          page: pageParam,
        },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const totalData = pages.reduce(
        (count, page) => count + (page?.data?.length || 0),
        0
      );

      if (lastPage?.total > totalData) {
        return pages.length + 1;
      }
      return undefined;
    },
  });

  const allEmployeeData: TBUEmployeeMember[] = useMemo(() => {
    if (employeeData?.pages) {
      return employeeData.pages.flatMap((page) => page.data);
    }
    return [];
  }, [employeeData?.pages]);

  const handleClickBack = () => {
    navigate(-1);
  };

  const handleClickAction = (record: TTableRecord, _actionType: string) => {
    const unassignedMember = buUnassignedMembers.find(
      ({ idMember }) => idMember === record?.idMember
    );
    if (unassignedMember) {
      setSelectedUnassignedMember(unassignedMember);
      setOpenEmploeeMemberModal(true);
    }
  };

  const handleOpenAssignConfirmationModal = () => {
    setOpenAssignConfirmationModal(true);
  };

  const handleCloseAssignConfirmationModal = () => {
    setOpenAssignConfirmationModal(false);
  };

  const handleSelectEmployee = (employee: TBUEmployeeMember) => {
    setSelectedEmployee(employee);
  };

  const handleClearSearchEmployeeKeyword = () => {
    setSearchEmployeeKeyword("");
  };

  const handleChangeSearchEmployee = (keyword: string) => {
    setSearchEmployeeKeyword(keyword);
  };

  const handleOpenEmployeeMemberModal = useCallback(() => {
    setOpenEmploeeMemberModal(true);
  }, []);

  const handleCloseEmployeeMemberModal = () => {
    setSelectedEmployee(undefined);
    setSelectedUnassignedMember(undefined);
    setOpenEmploeeMemberModal(false);
  };

  const handleSubmit = () => {
    if (!selectedEmployee?.idMember || !buSlug) return;

    if (!isSelectedAllPage) {
      let members: string[] = [];

      if (selectedUnassignedMember) {
        members = [selectedUnassignedMember.idMember];
      } else {
        members = buUnassignedMembers
          .filter((employee) => selectedRows.includes(employee.id))
          .map((employee) => employee.idMember);
      }

      mutateAddAssigmentMember({
        buId: buSlug,
        data: {
          idManager: selectedEmployee?.idMember,
          members,
        },
      });
      return;
    }
    const memberType = searchParams.filter?.memberType;

    mutateAddAssigmentAll({
      buId: buSlug,
      data: {
        idManager: selectedEmployee?.idMember,
        onlyUnassignMember: memberType === undefined,
        memberType: memberType,
        keyword: searchParams.filter?.keyword,
      },
    });
  };

  const handleSort = useCallback(
    (newSort: TSort) => {
      onSetSearchParams({
        sort: newSort,
      });
    },
    [onSetSearchParams]
  );

  const handleFilter = useCallback(
    (filter: Partial<TGetBUUnassignedMemberFilter>) => {
      onSetSearchParams({ ...filter });
    },
    [onSetSearchParams]
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      onSetSearchParams({
        page: newPage,
      });
    },
    [onSetSearchParams]
  );

  const handleChangePerPage = useCallback(
    (perPage: number) => {
      onSetSearchParams({
        pageSize: perPage,
      });
    },
    [onSetSearchParams]
  );

  const handleSearch = useCallback(
    (keyword: string) => {
      onSetSearchParams({
        keyword: keyword,
      });
    },
    [onSetSearchParams]
  );

  const handleRowChecked = useCallback((selectedIds: string[]) => {
    setSelectedRows(selectedIds);
  }, []);

  const handleSelectAllCurrentPage = useCallback(
    (isSelectAll: boolean) => {
      setSelectedRows(
        isSelectAll ? buUnassignedMembers.map((member) => member.id) : []
      );

      if (!isSelectAll) {
        setIsSelectedAllPage(false);
      }
    },
    [buUnassignedMembers]
  );

  const handleSelectAllPage = useCallback((isSelectAll: boolean) => {
    setIsSelectedAllPage(isSelectAll);
    setSelectedRows([]);
  }, []);

  useInfiniteScroll({
    listContainerRef: employeeListContainerRef,
    hasNextPage: employeeDataHasNextPage,
    isFetchingNextPage: isFetchingEmployeeDataNextPage,
    fetchNextPage: fetchEmployeeDataNextPage,
  });

  return {
    allEmployeeData,
    buUnassignedMembers,
    employeeListContainerRef,
    hasAssignStaffAccess,
    isFetchingEmployeeDataNextPage,
    isLoadingEmployeeData,
    isLoadingGetBUUnassignedMember,
    isSelectedAllPage,
    isSelectedRowExist: selectedRows.length > 0,
    openAssignConfirmationModal,
    openEmployeeMemberModal,
    pagination,
    searchEmployeeKeyword,
    searchParams,
    selectedEmployee,
    selectedRows,
    onChangePage: handleChangePage,
    onChangePerPage: handleChangePerPage,
    onChangeSearchEmployee: handleChangeSearchEmployee,
    onClearSearchEmployeeKeyword: handleClearSearchEmployeeKeyword,
    onClickAction: handleClickAction,
    onClickBack: handleClickBack,
    onCloseAssignConfirmationModal: handleCloseAssignConfirmationModal,
    onCloseSelectEmployeeMemberModal: handleCloseEmployeeMemberModal,
    onFilter: handleFilter,
    onOpenAssignConfirmationModal: handleOpenAssignConfirmationModal,
    onOpenSelectEmployeeMemberModal: handleOpenEmployeeMemberModal,
    onRowChecked: handleRowChecked,
    onSearch: handleSearch,
    onSelectAllCurrentPage: handleSelectAllCurrentPage,
    onSelectAllPage: handleSelectAllPage,
    onSelectEmployee: handleSelectEmployee,
    onSort: handleSort,
    onSubmit: handleSubmit,
  };
};
